import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Boxes,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FolderOpen,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Menu,
  MessageSquare,
  Network,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Upload,
  User,
  UserRoundPlus,
  Users,
} from 'lucide-react';
import site_logo from '@/assets/site_logo.jpg';
import { BRANDING_CHANGED_EVENT, getCachedBrandLogo, setCachedBrandLogo } from '@/utils/branding';
import { logger } from '@/utils/logger';
import { CATEGORY_CONFIG, getPathForModule, getPathForAdminModule } from '@/utils/moduleMapping';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { SidebarSkeleton } from '@/components/ui/sidebar-skeleton';
import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { SETUP_STATUS_EVENT } from '@/utils/setupStatus';
import { CLOUD_SOURCES_CHANGED_EVENT } from '@/services/cloudSourceService';
import { useAuth } from '@/context/AuthContext';
import { useAppContext } from '@/context/AppContext';
import { useYourChats, CHAT_CATEGORY_CONFIG } from '@/hooks/useYourChats';
import { ChatSearchModal } from '@/components/layout/ChatSearchModal';
import { ProfileSettingsDialog } from '@/components/layout/ProfileSettingsDialog';
import { ChangePasswordDialog } from '@/components/layout/ChangePasswordDialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useUserRoles } from '@/hooks/useUserRoles';

interface SubMenuItem {
  name: string;
  path: string;
  children?: Array<{ name: string; path: string }>;
}

interface MenuItem {
  name: string;
  icon: React.ComponentType<any>;
  path?: string;
  dropdown?: SubMenuItem[];
}

// AI Agents are now dynamically loaded based on user permissions

// Module-level cache so allowedModules survives sidebar remounts across layouts
let cachedAllowedModules: any = null;

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  dockMode: 'docked' | 'undocked';
  onDockToggle: () => void;
  autoHideEnabled: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggle,
  autoHideEnabled
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { sidebarRefreshTrigger } = useAppContext();
  const [allowedModules, setAllowedModules] = useState(cachedAllowedModules);

  // Get user roles for System Manager gating on KB admin pages
  const { roles: userRoles } = useUserRoles();
  const isSystemManager = userRoles?.some(r => r.name === 'System Manager');

  const [brandLogo, setBrandLogo] = useState<string | null>(getCachedBrandLogo);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const refreshBranding = useCallback(async () => {
    try {
      const res = await axios.get(getApiEndpoint('GET_DEFAULT_BRANDING'));
      const data = res?.data?.message?.data;
      // Fall back to the bundled logo when a save cleared the company logo.
      const url = data?.logo_url || null;
      setBrandLogo(url);
      setCachedBrandLogo(url); // keep the reload cache in sync
    } catch (err) {
      logger.error('Branding fetch failed:', err);
    }
  }, []);
  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  useEffect(() => {
    const handler = () => refreshBranding();
    window.addEventListener(BRANDING_CHANGED_EVENT, handler);
    return () => window.removeEventListener(BRANDING_CHANGED_EVENT, handler);
  }, [refreshBranding]);

  // User menu dialogs (moved from dashboard hero into the sidebar foot)
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // Workspace-setup lock: while initial setup is incomplete (Customise page
  // required fields / email account / provider keys), configuring admins see
  // ONLY the Customise link live - chat is hidden and the other nav entries
  // are frozen until setup is done. Refetched on route change so saving the
  // final field unlocks the sidebar on the next navigation.
  const [setupIncomplete, setSetupIncomplete] = useState(false);
  const refreshSetupStatus = useCallback(async () => {
    try {
      const res = await axios.get(getApiEndpoint('GET_SETUP_STATUS'), { withCredentials: true });
      const s = res.data?.message;
      setSetupIncomplete(!!(s && s.can_configure && !s.complete));
    } catch {
      /* fail open - never lock the sidebar on a broken check */
    }
  }, []);
  useEffect(() => {
    refreshSetupStatus();
  }, [location.pathname, refreshSetupStatus]);
  // Saves on the Customise page broadcast this event so the lock lifts
  // immediately - no navigation or manual reload needed.
  useEffect(() => {
    const handler = () => refreshSetupStatus();
    window.addEventListener(SETUP_STATUS_EVENT, handler);
    return () => window.removeEventListener(SETUP_STATUS_EVENT, handler);
  }, [refreshSetupStatus]);

  // When ANY cloud connector is authenticated, manual upload paths are
  // hidden: ingestion happens through the connector, so "Upload Documents"
  // (admin) and "Document Library" (chat) drop out of the nav. Mirrors the
  // Document Library page swapping its Ingest button for Sync buttons.
  const [hasConnectedSource, setHasConnectedSource] = useState(false);
  const refreshConnectedSource = useCallback(async () => {
    try {
      const res = await axios.get(getApiEndpoint('CLOUD_HAS_CONNECTED_SOURCE'), { withCredentials: true });
      setHasConnectedSource(!!res.data?.message?.connected);
    } catch {
      /* keep nav visible if the check fails */
    }
  }, []);
  // Re-check on mount and on every navigation so the manual-upload nav entry
  // tracks the current connector state (previously fetched once on mount, so
  // it only updated after a full page reload).
  useEffect(() => {
    refreshConnectedSource();
  }, [location.pathname, refreshConnectedSource]);
  // A connect/disconnect/delete broadcasts this event so the nav updates live,
  // without waiting for a navigation or reload.
  useEffect(() => {
    const handler = () => refreshConnectedSource();
    window.addEventListener(CLOUD_SOURCES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CLOUD_SOURCES_CHANGED_EVENT, handler);
  }, [refreshConnectedSource]);
  const performSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (e) {
      logger.error('Logout error:', e);
      navigate('/login');
    }
  };
  const handleSignOut = () => setShowSignOutConfirm(true);

  // Display name + role for sidebar foot
  const displayName = (auth.user?.full_name || auth.user?.name || 'Guest') as string;
  const displayInitials = (() => {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return displayName.slice(0, 2).toUpperCase();
  })();
  const displayRole = (() => {
    const u: any = auth.user || {};
    if (u.job_role && typeof u.job_role === 'string' && u.job_role.trim()) return u.job_role;
    const skip = new Set(['Guest', 'All']);
    const list: string[] = Array.isArray(u.roles) ? u.roles : [];
    const first = list.find((r) => r && !skip.has(r));
    return first || 'User';
  })();

  useEffect(() => {
    // Skip while auth is still resolving - fetching with no user produces an
    // empty role set, which used to lock the sidebar into the skeleton state.
    if (!auth.user) return;

    const fetchData = async () => {
      try {
        const user = auth.user;
        const allowedModulesResponse = await axios.get(getApiEndpoint("GET_ALLOWED_MODULES"));
        if (allowedModulesResponse?.data?.message?.status === "error") {
          throw Error(allowedModulesResponse?.data?.message?.message);
        }
        const userRoleSet = new Set(user?.roles || []);
        const matchingRoleEntries = allowedModulesResponse.data?.message?.data?.filter(
          (e: any) => userRoleSet.has(e?.role)
        ) || [];
        const moduleMap = new Map<string, any>();
        for (const entry of matchingRoleEntries) {
          for (const mod of (entry.modules || [])) {
            const key = mod.module;
            if (!moduleMap.has(key) || !moduleMap.get(key).show) {
              moduleMap.set(key, { ...mod });
            } else if (mod.sub_modules?.length) {
              const existing = moduleMap.get(key);
              const existingSubs = new Set((existing.sub_modules || []).map((s: any) => s.module));
              for (const sub of mod.sub_modules) {
                if (!existingSubs.has(sub.module)) {
                  existing.sub_modules = [...(existing.sub_modules || []), sub];
                }
              }
            }
          }
        }
        // Use [] (truthy) for "loaded but empty" so the skeleton clears even
        // when the user has no matching modules. Reserve null for "not loaded".
        const allowedModules = Array.from(moduleMap.values());
        cachedAllowedModules = allowedModules;
        setAllowedModules(allowedModules);
      } catch (err) {
        logger.error('Failed to fetch allowed modules:', err);
        // Fall back to cache (if any) so the sidebar doesn't stay in skeleton.
        setAllowedModules(cachedAllowedModules ?? []);
      }
    };
    fetchData();
  }, [sidebarRefreshTrigger, auth.user?.email, auth.user?.roles?.join(',')])


  const [isChatsHovered, setIsChatsHovered] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showSearchModal, setShowSearchModal] = useState(false);

  // CHAT | ADMIN sidebar tab. Follows the route (admin pages select the
  // ADMIN tab) but can be switched manually.
  const [sideTab, setSideTab] = useState<'chat' | 'admin'>(
    location.pathname.startsWith('/dashboard/admin') ? 'admin' : 'chat'
  );
  useEffect(() => {
    setSideTab(location.pathname.startsWith('/dashboard/admin') ? 'admin' : 'chat');
  }, [location.pathname]);

  // Setup lock pins the sidebar to the admin tab (chat is hidden anyway).
  useEffect(() => {
    if (setupIncomplete) setSideTab('admin');
  }, [setupIncomplete]);

  // Delayed hover handlers to prevent flickering when mouse crosses the gap
  const handleMenuEnter = useCallback((menuName: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredMenu(menuName);
  }, []);

  const handleMenuLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null);
      hoverTimeoutRef.current = null;
    }, 100);
  }, []);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  // Track expanded menu items for inline sub-menu display
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [expandedSubMenus, setExpandedSubMenus] = useState<string[]>([]);

  // Track if component has mounted to prevent transition jerk on navigation
  const [enableTransitions, setEnableTransitions] = useState(false);
  useEffect(() => {
    // Small delay to ensure initial render is complete before enabling transitions
    const timer = setTimeout(() => {
      setEnableTransitions(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Your Chats - using shared hook
  const {
    yourChatsExpanded,
    setYourChatsExpanded,
    expandedCategories,
    chatsByCategory,
    loadingByCategory,
    loadingMoreByCategory,
    paginationByCategory,
    totalSessionsCount,
    toggleCategory,
    loadMoreForCategory,
    getCategoryCount,
    deleteChat,
    refreshChats,
  } = useYourChats({ autoFetchOnMount: true, pageSize: 10 });

  // Refresh chat list when sidebar refresh is triggered (e.g. delete from detail page)
  useEffect(() => {
    if (sidebarRefreshTrigger > 0) {
      refreshChats();
    }
  }, [sidebarRefreshTrigger, refreshChats]);

  // Ref to suppress hover re-open after a click-to-close
  const hoverSuppressedUntil = useRef(0);

  // Accordion toggle functions - close others when one opens
  const toggleMenuExpanded = useCallback((menuName: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuName)
        ? [] // Close if clicking the same menu
        : [menuName] // Close others and open only this one
    );
    // Also close any expanded sub-menus when switching parent menus
    setExpandedSubMenus([]);
    // Close Chats when opening a menu
    setYourChatsExpanded(false);
  }, [setYourChatsExpanded]);

  // Toggle Chats and close other menus
  const toggleChatsExpanded = useCallback(() => {
    setYourChatsExpanded(prev => !prev);
    // Close other menus when opening Chats
    setExpandedMenus([]);
    setExpandedSubMenus([]);
  }, [setYourChatsExpanded]);

  // State for chat item hover (to show delete button)
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{
    sessionId: string;
    sessionTitle: string;
    categoryId: string;
  } | null>(null);

  // Handle delete chat - open confirmation dialog
  const handleDeleteChat = useCallback((
    e: React.MouseEvent,
    sessionId: string,
    sessionTitle: string,
    categoryId: string
  ) => {
    e.stopPropagation(); // Prevent opening the chat
    setChatToDelete({ sessionId, sessionTitle, categoryId });
    setDeleteDialogOpen(true);
  }, []);

  // Confirm delete chat
  const handleConfirmDelete = useCallback(async () => {
    if (!chatToDelete) return;

    setDeleteDialogOpen(false);
    setDeletingChatId(chatToDelete.sessionId);
    await deleteChat(chatToDelete.sessionId, chatToDelete.categoryId);
    setDeletingChatId(null);
    setChatToDelete(null);

    // If the deleted chat is currently being viewed, navigate to dashboard
    const params = new URLSearchParams(location.search);
    if (params.get('session_id') === chatToDelete.sessionId) {
      navigate('/dashboard');
    }
  }, [chatToDelete, deleteChat, location.search, navigate]);

  // Handle scroll to load more chats
  const handleChatScroll = useCallback((e: React.UIEvent<HTMLDivElement>, categoryId: string) => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const pagination = paginationByCategory[categoryId];

    // Load more when scrolled to bottom (within 20px threshold)
    if (scrollHeight - scrollTop - clientHeight < 20) {
      if (pagination && pagination.currentPage < pagination.totalPages && !loadingMoreByCategory[categoryId]) {
        loadMoreForCategory(categoryId);
      }
    }
  }, [paginationByCategory, loadingMoreByCategory, loadMoreForCategory]);

  // Format date relatively (today, yesterday, etc.) - matches mobile
  const formatRelativeDate = useCallback((dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  }, []);

  // Handle opening a chat session - all categories route to /dashboard
  // since General is the only chat category.
  const handleOpenChatSession = (session: any, _category: string) => {
    navigate(`/dashboard?session_id=${session.name}`);
  };

  // Name mapping for display (desktop-specific naming)
  const getDisplayName = (moduleName: string): string => {
    const nameMap: Record<string, string> = {
      'Dashboard': 'New Chat',
    };
    return nameMap[moduleName] || moduleName;
  };

  const renderNameWithTM = (name: string): React.ReactNode => name;

  // Flat nav items for the CHAT / ADMIN tab panels. Derived from the
  // backend-driven allowedModules payload so per-tenant/per-role gating is
  // preserved: no Administration module -> no ADMIN tab at all; the
  // Advanced group additionally requires the System Manager role.
  interface FlatNavItem {
    name: string;
    path: string;
    icon: React.ComponentType<{ className?: string }>;
    order?: number;
  }

  const { chatNavItems, adminNavItems, advancedNavItems, hasAdminArea } = useMemo(() => {
    const chatNav: FlatNavItem[] = [];
    const adminNav: FlatNavItem[] = [];
    const advancedNav: FlatNavItem[] = [];
    if (!allowedModules) {
      return { chatNavItems: chatNav, adminNavItems: adminNav, advancedNavItems: advancedNav, hasAdminArea: false };
    }

    let hasAdmin = false;

    // Design labels/icons/order keyed by route (renames live here:
    // File Library -> Document Library, My Company -> Customise,
    // Cloud Import -> Connectors).
    const ADMIN_PRESENTATION: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; order: number }> = {
      '/dashboard/admin/knowledge-base': { label: 'Document Library', icon: FolderOpen, order: 0 },
      '/dashboard/admin/upload': { label: 'Upload Documents', icon: Upload, order: 1 },
      '/dashboard/admin/cloud-import': { label: 'Connectors', icon: Network, order: 2 },
      // Folder Manager lives inside Upload Documents now - no own entry.
      '/dashboard/admin/users': { label: 'User Management', icon: Users, order: 4 },
      '/dashboard/admin/company': { label: 'Customise', icon: SlidersHorizontal, order: 5 },
    };
    const ADVANCED_PRESENTATION: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; order: number }> = {
      '/dashboard/admin/entity-types': { label: 'Entity Types', icon: Boxes, order: 0 },
      '/dashboard/admin/entity-qa': { label: 'Entity QA', icon: ClipboardCheck, order: 1 },
      '/dashboard/admin/review-queue': { label: 'Review Queue', icon: ListChecks, order: 2 },
    };

    allowedModules.forEach((moduleGroup: any) => {
      if (moduleGroup.show !== 1) return;
      const moduleName = moduleGroup.module;

      if (moduleGroup.sub_modules && moduleGroup.sub_modules.length > 0) {
        if (moduleName === 'Administration') {
          hasAdmin = true;
          const visibleSubModules = moduleGroup.sub_modules.filter((sub: any) => sub.show === 1);

          visibleSubModules.forEach((sub: any) => {
            if (sub.module === 'Knowledge Base') return; // expanded into pages below
            const path = getPathForAdminModule(sub.module);
            if (!path) return;
            const pres = ADMIN_PRESENTATION[path];
            adminNav.push({
              name: pres?.label || sub.module,
              path,
              icon: pres?.icon || Settings,
              order: pres?.order ?? 90,
            });
          });

          const hasKnowledgeBase = moduleGroup.sub_modules.some(
            (sub: any) => sub.module === 'Knowledge Base' && sub.show === 1
          );
          if (hasKnowledgeBase) {
            [
              // Admin Document Library always stays - it's the doc view, useful
              // regardless of how docs arrive. Only the manual Upload Documents
              // page is superseded by the connector once one is live, so it
              // drops out while a source is connected, alongside Cloud Import
              // (the connector surface).
              '/dashboard/admin/knowledge-base',
              ...(hasConnectedSource ? [] : ['/dashboard/admin/upload']),
              '/dashboard/admin/cloud-import',
            ].forEach((path) => {
              const pres = ADMIN_PRESENTATION[path];
              adminNav.push({ name: pres.label, path, icon: pres.icon, order: pres.order });
            });

            if (isSystemManager) {
              Object.entries(ADVANCED_PRESENTATION).forEach(([path, pres]) => {
                advancedNav.push({ name: pres.label, path, icon: pres.icon, order: pres.order });
              });
            }
          }
        } else {
          // Other grouped modules (tenant-enabled) land in the CHAT tab
          moduleGroup.sub_modules
            .filter((sub: any) => sub.show === 1)
            .forEach((sub: any) => {
              const path = getPathForModule(sub.module);
              if (path) chatNav.push({ name: getDisplayName(sub.module), path, icon: BookOpen, order: 50 });
            });
        }
      } else {
        if (moduleName === 'Dashboard') {
          // "+ New chat" pill already covers /dashboard
        } else if (moduleName === 'Documents') {
          if (!hasConnectedSource) {
            chatNav.push({ name: 'Document Library', path: '/dashboard/knowledge', icon: FolderOpen, order: 0 });
          }
        } else {
          const path = getPathForModule(moduleName);
          if (path) chatNav.push({ name: getDisplayName(moduleName), path, icon: BookOpen, order: 50 });
        }
      }
    });

    const byOrder = (a: FlatNavItem, b: FlatNavItem) => (a.order ?? 99) - (b.order ?? 99);
    adminNav.sort(byOrder);
    advancedNav.sort(byOrder);
    chatNav.sort(byOrder);

    return {
      chatNavItems: chatNav,
      adminNavItems: adminNav,
      advancedNavItems: advancedNav,
      hasAdminArea: hasAdmin && adminNav.length > 0,
    };
  }, [allowedModules, isSystemManager, hasConnectedSource]);

  // CHAT navigates to the chat page as well as selecting its panel. Toggling
  // alone left you on the admin page you were already on, so the tab-follows-
  // route effect snapped the selection straight back to ADMIN - which is why
  // ADMIN looked permanently active and the button appeared to "do nothing".
  //
  // ADMIN only reveals its panel: it has no page of its own (Document Library is
  // already the first item in that list), so navigating here would just duplicate
  // that entry.
  const goToChat = () => {
    setSideTab('chat');
    navigate('/dashboard');
  };

  const renderNavLink = (item: { name: string; path: string; icon: React.ComponentType<{ className?: string }> }, frozen = false) => {
    const Icon = item.icon;
    if (frozen) {
      // Setup lock: shown but inert until the workspace is configured.
      const frozenEl = (
        <div
          aria-disabled="true"
          className={(collapsed
            ? 'flex items-center justify-center h-10 w-10 rounded-lg '
            : 'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium w-full ') +
            'cursor-not-allowed text-muted-foreground/40 select-none'}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="truncate">{item.name}</span>}
        </div>
      );
      return (
        <Tooltip key={item.path}>
          <TooltipTrigger asChild>{frozenEl}</TooltipTrigger>
          <TooltipContent side="right" className="dark z-[100]">Complete setup in Customise to unlock</TooltipContent>
        </Tooltip>
      );
    }
    // isActive is computed here rather than via NavLink's `className` callback.
    // When collapsed the link is wrapped in <TooltipTrigger asChild>, and Radix's
    // Slot merges className with `[slot, child].filter(Boolean).join(' ')` - which
    // STRINGIFIES a function className into its own source code. NavLink then got a
    // garbage string instead of a callback, so the active state never applied and
    // the collapsed icon never highlighted (the expanded one, with no Tooltip
    // wrapper, worked fine). A plain string className survives the merge intact.
    const isActive =
      location.pathname === item.path ||
      location.pathname.startsWith(item.path + '/');
    const link = (
      <NavLink
        to={item.path}
        className={
          (collapsed
            ? 'flex items-center justify-center h-10 w-10 rounded-lg transition-colors '
            : 'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors w-full ') +
          (isActive
            ? 'bg-primary/10 text-foreground'
            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground')
        }
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.name}</span>}
      </NavLink>
    );
    if (collapsed) {
      return (
        <Tooltip key={item.path}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="dark z-[100]">{item.name}</TooltipContent>
        </Tooltip>
      );
    }
    return <React.Fragment key={item.path}>{link}</React.Fragment>;
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="relative flex">
        <div className={`dv3-sidebar dark bg-background text-foreground h-screen border-r border-border relative ${
          enableTransitions ? 'transition-all duration-200 ease-out' : ''
        } ${collapsed ? 'w-16' : 'w-72'} ${autoHideEnabled ? 'fixed left-0 top-0 z-50 shadow-xl' : ''}`} style={{
          willChange: enableTransitions ? 'width' : 'auto',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden'
        }}>
          {/* Container with flex column to stretch full height */}
          <div className="flex flex-col h-full overflow-hidden sidebar-no-focus-ring">

            {/* Top Section - hamburger | centered logo | search.
                py-4 + h-8 buttons → 64px total to match the chat header in Interface.tsx (p-4). */}
            <div className={`flex items-center ${collapsed ? 'justify-center px-2 py-4' : 'justify-between px-3 py-4 gap-2'}`}>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggle}
                className="text-foreground hover:bg-muted h-8 w-8 shrink-0"
                aria-label="Toggle sidebar"
              >
                <Menu className="h-4 w-4" />
              </Button>

              {!collapsed && (
                <div className="dv3-brand-row flex-1 flex justify-center min-w-0">
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      borderRadius: 8,
                      padding: '4px 10px',
                    }}
                  >
                    {/* Fade lives on the <img>, not this wrapper: an ancestor
                        at fractional opacity creates a stacking context that
                        traps the blend, flashing the black box mid-fade. */}
                    <img
                      src={brandLogo || site_logo}
                      alt="Brand logo"
                      style={{
                        height: 36,
                        width: 'auto',
                        filter: 'invert(1)',
                        mixBlendMode: 'screen',
                        opacity: logoLoaded ? 1 : 0,
                        transition: 'opacity 200ms ease',
                      }}
                      onLoad={() => setLogoLoaded(true)}
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        if (img.src !== site_logo && !img.src.endsWith(site_logo)) {
                          img.src = site_logo;
                        } else {
                          img.style.display = 'none';
                        }
                      }}
                    />
                  </span>
                </div>
              )}

              {!collapsed && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-foreground hover:bg-muted focus:outline-none focus-visible:ring-0 h-8 w-8 shrink-0"
                  aria-label="Search"
                  onClick={() => setShowSearchModal(true)}
                >
                  <Search className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* CHAT | ADMIN tab strip - only when the user has an admin area */}
            {hasAdminArea && (
              collapsed ? (
                // Segmented control, NOT nav icons. Collapsed, these used to be
                // the same size/shape/highlight as the page links below, so they
                // read as "pages you can open" - and clicking Admin while already
                // in Admin looked like a dead click. Enclosing them in a track
                // with a raised selected item (and a divider before the real nav)
                // makes them read as a Chat/Admin mode switch instead.
                <div className="px-2 pt-2">
                  <div
                    role="tablist"
                    aria-orientation="vertical"
                    aria-label="Sidebar section"
                    className="flex flex-col items-center gap-0.5 rounded-lg bg-muted/40 p-1"
                  >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={sideTab === 'chat'}
                        aria-label="Chat"
                        disabled={setupIncomplete}
                        onClick={goToChat}
                        className={'flex items-center justify-center h-8 w-8 rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-40 ' + (sideTab === 'chat' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="dark z-[100]">Switch to Chat</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={sideTab === 'admin'}
                        aria-label="Admin"
                        onClick={() => setSideTab('admin')}
                        className={'flex items-center justify-center h-8 w-8 rounded-md transition-colors ' + (sideTab === 'admin' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                      >
                        <ShieldCheck className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="dark z-[100]">Switch to Admin</TooltipContent>
                  </Tooltip>
                  </div>
                  {/* Separates the mode switch from the page links below it. */}
                  <div className="mx-auto mt-2 h-px w-8 bg-border" />
                </div>
              ) : (
                <div className="flex items-stretch border-b border-border px-3">
                  <button
                    type="button"
                    disabled={setupIncomplete}
                    onClick={goToChat}
                    className={'flex-1 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] border-b-2 -mb-px transition-colors disabled:cursor-not-allowed disabled:opacity-40 ' + (sideTab === 'chat' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
                  >
                    Chat
                  </button>
                  <button
                    type="button"
                    onClick={() => setSideTab('admin')}
                    className={'flex-1 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] border-b-2 -mb-px transition-colors ' + (sideTab === 'admin' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
                  >
                    Admin
                  </button>
                </div>
              )
            )}

            {/* ── CHAT panel ─────────────────────────────────────────── */}
            {!setupIncomplete && (!hasAdminArea || sideTab === 'chat') && (
              <>
                {/* + New chat primary pill - wired to existing /dashboard route. */}
                <div className={collapsed ? 'px-2 pt-4 pb-2 flex justify-center' : 'px-3 pt-4 pb-3'}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className={collapsed
                          ? 'dv3-new-chat !w-10 !h-10 !p-0 !rounded-full'
                          : 'dv3-new-chat'}
                        onClick={() => navigate('/dashboard')}
                        aria-label="New chat"
                      >
                        <Plus className={collapsed ? 'h-5 w-5' : 'h-4 w-4'} strokeWidth={2.5} />
                        {!collapsed && <span>New chat</span>}
                      </button>
                    </TooltipTrigger>
                    {collapsed && <TooltipContent side="right" className="dark z-[100]">New chat</TooltipContent>}
                  </Tooltip>
                </div>

                <div
                  className={'flex-1 min-h-0 overflow-y-auto sidebar-nav-scroll px-2 py-2 ' + (collapsed ? 'flex flex-col items-center' : '')}
                  onScroll={!collapsed ? (e) => handleChatScroll(e, CHAT_CATEGORY_CONFIG[0].id) : undefined}
                >
                  {!allowedModules ? (
                    <SidebarSkeleton collapsed={collapsed} />
                  ) : (
                    <nav className={'space-y-1 ' + (collapsed ? 'flex flex-col items-center w-full' : '')}>
                      {chatNavItems.map((item) => renderNavLink(item))}
                    </nav>
                  )}

                  {/* RECENT chats - always visible on the chat tab */}
                  {!collapsed && (
                    <div className="mt-5">
                      <p className="px-3 pb-1 flex items-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                        Recent
                        {totalSessionsCount > 0 && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-primary/10 rounded-full text-primary normal-case tracking-normal">
                            {totalSessionsCount}
                          </span>
                        )}
                      </p>
                      <div className="space-y-0.5">
                        {loadingByCategory[CHAT_CATEGORY_CONFIG[0].id] ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : (chatsByCategory[CHAT_CATEGORY_CONFIG[0].id] || []).length > 0 ? (
                          <>
                            {(chatsByCategory[CHAT_CATEGORY_CONFIG[0].id] || []).map((session: any) => (
                              <div
                                key={session.name}
                                className="relative group"
                                onMouseEnter={() => setHoveredChatId(session.name)}
                                onMouseLeave={() => setHoveredChatId(null)}
                              >
                                <button
                                  onClick={() => handleOpenChatSession(session, CHAT_CATEGORY_CONFIG[0].id)}
                                  className="w-full text-left rounded-lg px-3 py-1.5 hover:bg-muted/50 focus:outline-none pr-7"
                                  disabled={deletingChatId === session.name}
                                >
                                  <p className="text-xs text-foreground/80 truncate">{session.session_title || session.title || session.name}</p>
                                  <p className="text-[10px] text-muted-foreground">{formatRelativeDate(session.started_at)}</p>
                                </button>
                                {(hoveredChatId === session.name || deletingChatId === session.name) && (
                                  <button
                                    onClick={(e) => handleDeleteChat(e, session.name, session.session_title || session.title, CHAT_CATEGORY_CONFIG[0].id)}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-red-500/10 rounded transition-colors"
                                    title="Delete chat"
                                    disabled={deletingChatId === session.name}
                                  >
                                    {deletingChatId === session.name ? (
                                      <Loader2 className="h-3 w-3 animate-spin text-red-400" />
                                    ) : (
                                      <Trash2 className="h-3 w-3 text-red-400 hover:text-red-300" />
                                    )}
                                  </button>
                                )}
                              </div>
                            ))}
                            {loadingMoreByCategory[CHAT_CATEGORY_CONFIG[0].id] && (
                              <div className="flex items-center justify-center py-2">
                                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-muted-foreground px-3 py-2">No chats yet</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Collapsed: recent chats via flyout */}
                  {collapsed && (
                    <DropdownMenu open={isChatsHovered}>
                      <DropdownMenuTrigger asChild>
                        <div
                          onMouseEnter={() => setIsChatsHovered(true)}
                          onMouseLeave={() => setIsChatsHovered(false)}
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Recent chats"
                            className="mt-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg h-10 w-10"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        side="right"
                        align="start"
                        sideOffset={4}
                        avoidCollisions={false}
                        className="dark bg-background text-foreground z-50 shadow-lg w-56 p-0"
                        onMouseEnter={() => setIsChatsHovered(true)}
                        onMouseLeave={() => setIsChatsHovered(false)}
                      >
                        <div className="px-2 py-1.5 text-sm font-medium border-b border-border flex items-center">
                          <span>Recent Chats</span>
                          {totalSessionsCount > 0 && (
                            <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-primary/10 rounded-full text-primary">
                              {totalSessionsCount}
                            </span>
                          )}
                        </div>
                        <div className="py-1">
                          <div
                            className="sidebar-chat-scroll"
                            style={{ maxHeight: '320px', overflowY: (chatsByCategory[CHAT_CATEGORY_CONFIG[0].id] || []).length > 10 ? 'scroll' : 'auto' }}
                            onScroll={(e) => handleChatScroll(e, CHAT_CATEGORY_CONFIG[0].id)}
                          >
                            {loadingByCategory[CHAT_CATEGORY_CONFIG[0].id] ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                              </div>
                            ) : (chatsByCategory[CHAT_CATEGORY_CONFIG[0].id] || []).length > 0 ? (
                              <>
                                {(chatsByCategory[CHAT_CATEGORY_CONFIG[0].id] || []).map((session: any) => (
                                  <div
                                    key={session.name}
                                    className="relative group"
                                    onMouseEnter={() => setHoveredChatId(session.name)}
                                    onMouseLeave={() => setHoveredChatId(null)}
                                  >
                                    <button
                                      className="w-full text-left px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer focus:outline-none pr-7"
                                      onClick={() => handleOpenChatSession(session, CHAT_CATEGORY_CONFIG[0].id)}
                                      disabled={deletingChatId === session.name}
                                    >
                                      <p className="text-xs text-foreground/80 truncate">{session.session_title || session.title || session.name}</p>
                                    </button>
                                    {(hoveredChatId === session.name || deletingChatId === session.name) && (
                                      <button
                                        onClick={(e) => handleDeleteChat(e, session.name, session.session_title || session.title, CHAT_CATEGORY_CONFIG[0].id)}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-red-500/10 rounded transition-colors"
                                        title="Delete chat"
                                        disabled={deletingChatId === session.name}
                                      >
                                        {deletingChatId === session.name ? (
                                          <Loader2 className="h-3 w-3 animate-spin text-red-400" />
                                        ) : (
                                          <Trash2 className="h-3 w-3 text-red-400 hover:text-red-300" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                ))}
                                {loadingMoreByCategory[CHAT_CATEGORY_CONFIG[0].id] && (
                                  <div className="flex items-center justify-center py-2">
                                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="text-xs text-muted-foreground px-2 py-2">No chats yet</p>
                            )}
                          </div>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </>
            )}

            {/* ── ADMIN panel ────────────────────────────────────────── */}
            {hasAdminArea && sideTab === 'admin' && (
              <div className={'flex-1 min-h-0 overflow-y-auto sidebar-nav-scroll px-2 py-4 ' + (collapsed ? 'flex flex-col items-center' : '')}>
                {!allowedModules ? (
                  <SidebarSkeleton collapsed={collapsed} />
                ) : (
                  <>
                    <nav className={'space-y-1 ' + (collapsed ? 'flex flex-col items-center w-full' : '')}>
                      {adminNavItems.map((item) => renderNavLink(item, setupIncomplete && item.path !== '/dashboard/admin/company'))}
                    </nav>
                    {advancedNavItems.length > 0 && (
                      <>
                        {collapsed ? (
                          <div className="my-3 h-px w-8 bg-border mx-auto" />
                        ) : (
                          <p className="px-3 pt-5 pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                            Advanced
                          </p>
                        )}
                        <nav className={'space-y-1 ' + (collapsed ? 'flex flex-col items-center w-full' : '')}>
                          {advancedNavItems.map((item) => renderNavLink(item, setupIncomplete))}
                        </nav>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Bottom Section - User card (v3: name + role) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {!collapsed ? (
                  <button
                    type="button"
                    aria-label={displayName}
                    className="dv3-sidebar-foot bg-transparent border-l-0 border-r-0 border-b-0 text-left cursor-pointer w-full font-[inherit] text-[inherit]"
                  >
                    <span className="dv3-sf-av" aria-hidden="true">
                      {auth.user?.avatar ? (
                        <img
                          src={auth.user.avatar}
                          alt={displayName}
                          onError={(e) => {
                            const img = e.currentTarget as HTMLImageElement;
                            img.style.display = 'none';
                          }}
                        />
                      ) : (
                        displayInitials
                      )}
                    </span>
                    <div className="dv3-sf-meta">
                      <div className="dv3-sf-name">{displayName}</div>
                      <div className="dv3-sf-role">{displayRole}</div>
                    </div>
                  </button>
                ) : (
                  <button
                    type="button"
                    aria-label={displayName}
                    className="mt-auto bg-transparent border-l-0 border-r-0 border-b-0 border-t border-border p-3 flex justify-center w-full cursor-pointer"
                  >
                    <span className="dv3-sf-av" aria-hidden="true">{displayInitials}</span>
                  </button>
                )}
              </DropdownMenuTrigger>
              {/* avoidCollisions was false, which told Radix not to nudge the menu
                  away from the viewport edge - so Sign Out sat flush against the
                  bottom of the screen. Letting it collide-detect with padding
                  keeps a gap below it. */}
              <DropdownMenuContent
                align="end"
                side={collapsed ? 'right' : 'top'}
                sideOffset={collapsed ? 8 : 4}
                collisionPadding={12}
                className={`dark bg-background text-foreground z-50 shadow-lg p-0 ${collapsed ? 'w-56' : 'w-[280px]'}`}
              >
                <div className="px-2 py-1.5 text-sm font-medium border-b border-border">
                  Account
                </div>
                <div className="py-1">
                  <DropdownMenuItem onClick={() => setShowProfileSettings(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Profile Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-red-400 focus:text-red-400">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>



        {/* Profile + Change Password dialogs (triggered from the user card) */}
        <ProfileSettingsDialog open={showProfileSettings} onOpenChange={setShowProfileSettings} />
        <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />

        {/* Chat Search Modal */}
        <ChatSearchModal
          open={showSearchModal}
          onOpenChange={setShowSearchModal}
        />

        {/* Delete Chat Confirmation Dialog */}
        <ConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleConfirmDelete}
          title="Delete Chat"
          description={`Delete "${chatToDelete?.sessionTitle || 'this chat'}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="destructive"
          contentClassName="dark bg-background text-foreground"
        />

        {/* Sign Out Confirmation */}
        <ConfirmationDialog
          open={showSignOutConfirm}
          onOpenChange={setShowSignOutConfirm}
          onConfirm={performSignOut}
          title="Sign out?"
          description="You'll need to sign in again to access your account."
          confirmText="Sign Out"
          cancelText="Cancel"
          confirmVariant="destructive"
          contentClassName="dark bg-background text-foreground"
        />


      </div>
    </TooltipProvider>
  );
};
